<script>
  // Presentational shell for one FrameX pipeline step: a tab row that both
  // names the step and expands its detail. The work is a loop, not a wizard —
  // nothing here is ever locked, dimmed or checked off, and a closed row
  // carries no state at all: the counts report progress, not the row's colour.
  import { createEventDispatcher } from "svelte";

  export let number;
  export let title;
  /** Right-aligned parenthesised quantity, or "" for no count (step 1). */
  export let count = "";
  /** Whether this row is open. The only thing that colours it. */
  export let expanded = false;

  const dispatch = createEventDispatcher();

  // Ties the row to the block it controls for screen readers. Step numbers are
  // unique within the one panel that renders these.
  $: detailId = `fx-step-detail-${number}`;
</script>

<div class="fx-step" class:fx-step-active={expanded}>
  <!-- A real <button>, so Enter/Space and the focus ring come from the
       platform. The badge is decorative — the title already names the step —
       but the count stays inside the button so it's read as part of the label. -->
  <button
    class="fx-step-row"
    aria-expanded={expanded}
    aria-controls={detailId}
    on:click={() => dispatch("toggle")}>
    <span class="fx-step-badge" aria-hidden="true">{number}</span>
    <span class="fx-step-title">{title}</span>
    {#if count}
      <span class="fx-step-count">{count}</span>
    {/if}
  </button>

  {#if expanded}
    <div class="fx-step-detail" id={detailId}>
      <slot />
    </div>
  {/if}
</div>

<style lang="scss">
  .fx-step-row {
    // all:unset strips the UA button chrome (and box-sizing, display and the
    // focus ring with it) — each is restored below. Matches .fx-chip's reset.
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 12px;
    // Visible at rest, so a row reads as a control rather than a label. Stays
    // 1px through hover and the active state, which only change its colour — so
    // opening a row can't shift its height or its content by a pixel.
    border: 1px solid var(--fx-accent-strip-border);
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    text-align: left;
    background: #fff;
    color: var(--fx-text-alt);

    // Fill and border together — the wash alone was too subtle to register as a
    // hover. The border deepens to the open row's blue while the fill stays
    // lighter than --fx-accent-tint, so hover still reads as "clickable" rather
    // than "you are here". Stays 1px, so nothing shifts by a pixel.
    &:hover {
      background: var(--fx-step-active-bg);
      border-color: var(--fx-accent);
    }

    &:focus-visible {
      outline: 2px solid var(--fx-accent);
      outline-offset: -2px;
    }
  }

  // The open row and its detail block must read as one outlined object: same
  // width, same stroke, no gap, no line across the seam. The bottom border is
  // transparent rather than removed so the row keeps its geometry.
  .fx-step-active .fx-step-row {
    background: var(--fx-accent-tint);
    color: var(--fx-accent-hover);
    border-color: var(--fx-accent);
    border-bottom-color: transparent;
    border-radius: 6px 6px 0 0;
  }

  .fx-step-badge {
    flex: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    // Soft blue fill, with the number in the stronger blue the circle turns when
    // the step is open — so rest and open read as one object. The dark number is
    // what keeps the badge legible on hover, where the fill and the row's wash
    // sit only a shade apart. Active overrides both below.
    background: var(--fx-accent-tint);
    color: var(--fx-accent);

    // White on hover, so the circle lifts off the row's wash instead of sinking
    // into it — the soft blue and the wash sit only a shade apart.
    .fx-step-row:hover & {
      background: #fff;
    }

    // Scoped through .fx-step-row so it ties with the hover rule above on
    // specificity and wins on order — hovering the open step keeps its solid
    // blue circle rather than whitening it.
    .fx-step-active .fx-step-row & {
      background: var(--fx-accent);
      color: #fff;
    }
  }

  .fx-step-title {
    flex: 1;
    min-width: 0;
  }

  .fx-step-count {
    flex: none;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--fx-text-alt);

    .fx-step-active & {
      color: var(--fx-accent-hover);
    }
  }

  .fx-step-detail {
    background: var(--fx-step-active-bg);
    border: 1px solid var(--fx-accent);
    border-top: none;
    border-radius: 0 0 6px 6px;
    padding: 10px 12px;
  }
</style>
